import { useState } from "react";
import { motion } from "framer-motion";
import { Watch, Link2, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { CoachLine } from "./parts";

const PERKS = [
  "Your body basics — age, weight, resting heart rate",
  "Your main sport & recent training volume",
  "Fitness markers — VO₂max, threshold pace/HR",
  "Then keeps syncing sleep, HRV & readiness daily",
];

export default function GarminIntro({ onConnected, onSkip }) {
  const [mode, setMode] = useState("intro"); // intro | form | mfa
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const summarize = (af = {}) => {
    const bits = [];
    if (af.primary_sport) bits.push(af.primary_sport);
    if (af.age) bits.push(`${af.age} yrs`);
    if (af.weight_kg) bits.push(`${af.weight_kg} kg`);
    if (af.vo2max_running) bits.push(`VO₂max ${af.vo2max_running}`);
    return bits.length ? bits.join(" · ") : "your profile";
  };

  const connect = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/garmin/onboarding/connect", { email, password });
      if (data.needs_mfa) {
        setSessionId(data.session_id); setMode("mfa");
        toast("Enter the code Garmin just sent you");
      } else {
        toast("Garmin connected", { description: `Pulled ${summarize(data.autofill)}` });
        onConnected(data.autofill || {});
      }
    } catch (e) {
      const m = formatApiErrorDetail(e.response?.data?.detail) || "Connection failed";
      setErr(m); toast("Couldn't connect", { description: m });
    } finally { setBusy(false); }
  };

  const submitMfa = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/garmin/onboarding/mfa", { session_id: sessionId, code });
      toast("Garmin connected", { description: `Pulled ${summarize(data.autofill)}` });
      onConnected(data.autofill || {});
    } catch (e) {
      const m = formatApiErrorDetail(e.response?.data?.detail) || "MFA failed";
      setErr(m); toast("MFA failed", { description: m });
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="garmin-intro">
      <CoachLine subtitle="Connect your watch and I'll build your profile for you — you just confirm. Or set it up by hand, your call.">
        Hey, I'm Caden. Let's skip the typing — connect your Garmin?
      </CoachLine>

      <div className="rounded-2xl border border-primary/40 bg-primary/5 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="brand-gradient w-11 h-11 rounded-xl flex items-center justify-center shrink-0">
            <Watch className="text-white" size={22} />
          </div>
          <div>
            <p className="font-heading font-bold text-foreground">Connect Garmin</p>
            <p className="text-xs text-muted-foreground">Fastest way to get started</p>
          </div>
        </div>

        {mode === "intro" && (
          <>
            <ul className="space-y-2.5 mb-5" data-testid="garmin-intro-perks">
              {PERKS.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm text-foreground">
                  <Sparkles size={15} className="text-brand-orange mt-0.5 shrink-0" /> {p}
                </li>
              ))}
            </ul>
            <Button onClick={() => setMode("form")} data-testid="garmin-intro-start" className="w-full h-11 rounded-xl font-semibold">
              <Link2 size={16} className="mr-1.5" /> Connect my Garmin
            </Button>
          </>
        )}

        {mode === "form" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            <Input data-testid="garmin-intro-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Garmin email" className="h-11" />
            <Input data-testid="garmin-intro-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Garmin password" className="h-11" />
            <Button onClick={connect} disabled={busy || !email || !password} data-testid="garmin-intro-connect" className="w-full h-11 rounded-xl font-semibold">
              {busy ? "Connecting…" : "Connect & autofill"}
            </Button>
            {err && <p className="text-sm text-destructive leading-relaxed" data-testid="garmin-intro-error">{err}</p>}
          </motion.div>
        )}

        {mode === "mfa" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
            <p className="text-sm text-foreground">Enter the verification code Garmin sent you:</p>
            <Input data-testid="garmin-intro-mfa-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" className="h-11" />
            <Button onClick={submitMfa} disabled={busy || !code} data-testid="garmin-intro-mfa-btn" className="w-full h-11 rounded-xl font-semibold">
              {busy ? "Verifying…" : "Verify & connect"}
            </Button>
            {err && <p className="text-sm text-destructive leading-relaxed" data-testid="garmin-intro-error">{err}</p>}
          </motion.div>
        )}

        <p className="flex items-center gap-1.5 text-xs text-muted-foreground mt-4">
          <ShieldCheck size={13} /> Your password is used once for a secure token — we never store it.
        </p>
      </div>

      <button onClick={onSkip} data-testid="garmin-intro-skip" className="w-full mt-5 flex items-center justify-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
        Set up manually instead <ArrowRight size={15} />
      </button>
    </div>
  );
}
